/**
 * Admin Users Page — list end-users, manage tokens, search by email,
 * toggle status, and delete with fund repayment check.
 */

import React, { useEffect, useState, useCallback } from 'react';
import * as adminService from '../../services/admin.service';
import {
  Users,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Eye,
  X,
  Coins,
  Search,
  Trash2,
  ShieldAlert,
} from 'lucide-react';

export default function AdminUsersPage() {
  const [users, setUsers] = useState<adminService.AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  // Email search
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<adminService.AdminUser | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // User detail drawer
  const [detail, setDetail] = useState<adminService.AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Credit tokens form
  const [creditTokenId, setCreditTokenId] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [crediting, setCrediting] = useState(false);
  const [creditSuccess, setCreditSuccess] = useState('');

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<adminService.AdminUser | null>(null);
  const [deleteFundsConfirmed, setDeleteFundsConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadUsers = useCallback((cursor?: string) => {
    setLoading(true);
    setIsSearchMode(false);
    setSearchResult(null);
    adminService
      .listUsers(25, cursor)
      .then((result) => {
        setUsers((prev) => (cursor ? [...prev, ...result.users] : result.users));
        setNextCursor(result.nextCursor);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ── Email search ──
  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    setError('');
    setSearchResult(null);
    try {
      const user = await adminService.searchUserByEmail(searchEmail.trim());
      setSearchResult(user);
      setIsSearchMode(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setIsSearchMode(true);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchEmail('');
    setSearchResult(null);
    setIsSearchMode(false);
    loadUsers();
  };

  // ── Detail drawer ──
  const openDetail = async (userId: string) => {
    setDetailLoading(true);
    setCreditSuccess('');
    setCreditTokenId('');
    setError('');
    try {
      const user = await adminService.getUserDetail(userId);
      setDetail(user);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load user details');
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Credit tokens ──
  const handleCredit = async () => {
    if (!creditTokenId || !creditAmount || !creditReason) return;
    setCrediting(true);
    setCreditSuccess('');
    try {
      const result = await adminService.creditTokens({
        tokenId: creditTokenId,
        amount: Number(creditAmount),
        reason: creditReason,
      });
      setCreditSuccess(`Credited! New balance: ${result.newBalance.toLocaleString()} tokens`);
      setCreditAmount('');
      setCreditReason('');
      if (detail) {
        const refreshed = await adminService.getUserDetail(detail.userId);
        setDetail(refreshed);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Credit failed');
    } finally {
      setCrediting(false);
    }
  };

  // ── Status toggle ──
  const handleStatusToggle = async (user: adminService.AdminUser) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      await adminService.updateUser(user.userId, { status: newStatus });
      if (isSearchMode && searchResult) {
        setSearchResult({ ...searchResult, status: newStatus });
      } else {
        loadUsers();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget || !deleteFundsConfirmed) return;
    setDeleting(true);
    setError('');
    try {
      await adminService.deleteUser(deleteTarget.userId, true);
      setDeleteTarget(null);
      setDeleteFundsConfirmed(false);
      setDetail(null);
      if (isSearchMode) {
        clearSearch();
      } else {
        loadUsers();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Which users to display — search result or full list
  const displayUsers = isSearchMode
    ? (searchResult ? [searchResult] : [])
    : users;

  return (
    <div className="apikeys-page">
      <div className="page-header">
        <div>
          <h1><Users size={22} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />Users</h1>
          <p>End-user accounts and token balances</p>
        </div>
      </div>

      {/* ── Email Search Bar ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label>Search by Email</label>
          <input
            placeholder="user@example.com"
            type="email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <button className="btn btn-accent btn-sm" onClick={handleSearch} disabled={searching || !searchEmail.trim()} style={{ height: 40 }}>
          <Search size={14} /> {searching ? 'Searching…' : 'Search'}
        </button>
        {isSearchMode && (
          <button className="btn btn-ghost btn-sm" onClick={clearSearch} style={{ height: 40 }}>
            Clear
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} /> {error}
          <button className="btn btn-ghost btn-sm" onClick={() => setError('')} style={{ marginLeft: 'auto' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => { setDeleteTarget(null); setDeleteFundsConfirmed(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldAlert size={20} style={{ color: 'var(--danger)' }} /> Delete User
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: '8px 0 16px' }}>
              This will permanently delete <strong>{deleteTarget.email}</strong>, deactivate all their tokens,
              and remove their Cognito account. This action cannot be undone.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, marginBottom: 16 }}>
              <div><strong>Name:</strong> {deleteTarget.fullName || '—'}</div>
              <div><strong>Status:</strong> {deleteTarget.status}</div>
              <div><strong>Created:</strong> {new Date(deleteTarget.createdAt).toLocaleDateString()}</div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '12px 16px', background: 'var(--bg-soft)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <input
                type="checkbox"
                checked={deleteFundsConfirmed}
                onChange={(e) => setDeleteFundsConfirmed(e.target.checked)}
                style={{ accentColor: 'var(--danger)' }}
              />
              <span>I confirm that all funds have been repaid and reconciled for this user account.</span>
            </label>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setDeleteTarget(null); setDeleteFundsConfirmed(false); }}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={!deleteFundsConfirmed || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── User Detail Drawer ── */}
      {(detail || detailLoading) && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>User Detail</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setDetail(null)}>
                <X size={16} />
              </button>
            </div>

            {detailLoading && <div className="page-loader">Loading…</div>}

            {detail && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12, fontSize: 13 }}>
                  <div><strong>Email:</strong> {detail.email}</div>
                  <div><strong>Name:</strong> {detail.fullName || '—'}</div>
                  <div><strong>Status:</strong> {detail.status}</div>
                  <div><strong>Created:</strong> {new Date(detail.createdAt).toLocaleDateString()}</div>
                </div>

                <h3 style={{ margin: '20px 0 8px', fontSize: 15 }}>
                  <Coins size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                  Tokens
                </h3>

                <table className="data-table" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Prefix</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Last Used</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.tokens.map((t) => (
                      <tr key={t.tokenId}>
                        <td><code style={{ fontSize: 11 }}>{t.tokenPrefix}…</code></td>
                        <td className="cell-primary">{t.balance.toLocaleString()}</td>
                        <td>
                          <span className={`status-dot ${t.status === 'active' ? 'active' : 'inactive'}`} />
                          {t.status}
                        </td>
                        <td>{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : 'Never'}</td>
                        <td>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setCreditTokenId(t.tokenId)}
                            title="Credit tokens"
                          >
                            <Coins size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {detail.tokens.length === 0 && (
                      <tr>
                        <td colSpan={5} className="empty-state">No tokens.</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Credit tokens form */}
                {creditTokenId && (
                  <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-card)', borderRadius: 8 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>
                      Credit Tokens to {creditTokenId.substring(0, 8)}…
                    </h4>

                    {creditSuccess && (
                      <div className="alert alert-success" style={{ marginBottom: 12 }}>
                        <CheckCircle2 size={14} /> {creditSuccess}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        placeholder="Amount"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        min={1}
                        style={{ width: 120 }}
                      />
                      <input
                        placeholder="Reason (e.g. goodwill credit)"
                        value={creditReason}
                        onChange={(e) => setCreditReason(e.target.value)}
                        style={{ flex: 1, minWidth: 160 }}
                      />
                      <button
                        className="btn btn-accent btn-sm"
                        onClick={handleCredit}
                        disabled={crediting || !creditAmount || !creditReason}
                      >
                        {crediting ? 'Crediting…' : 'Credit'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setCreditTokenId('')}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Users Table ── */}
      {isSearchMode && searchResult === null && !searching && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
          No user found with that email address.
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayUsers.map((u) => (
              <tr key={u.userId}>
                <td className="cell-primary">{u.fullName || '—'}</td>
                <td>{u.email}</td>
                <td>
                  <span className={`status-dot ${u.status === 'active' ? 'active' : 'inactive'}`} />
                  {u.status}
                </td>
                <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td className="actions-cell">
                  <button className="btn btn-ghost btn-sm" onClick={() => openDetail(u.userId)} title="View detail">
                    <Eye size={14} />
                  </button>
                  <button
                    className={`btn btn-sm ${u.status === 'active' ? 'btn-ghost' : 'btn-accent'}`}
                    onClick={() => handleStatusToggle(u)}
                  >
                    {u.status === 'active' ? 'Suspend' : 'Activate'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setDeleteTarget(u)}
                    title="Delete user"
                    style={{ color: 'var(--danger)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {displayUsers.length === 0 && !loading && !isSearchMode && (
              <tr>
                <td colSpan={5} className="empty-state">No users yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && <div className="page-loader">Loading…</div>}

      {!isSearchMode && nextCursor && !loading && (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <button className="btn btn-ghost" onClick={() => loadUsers(nextCursor)}>
            Load More <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
